import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const spinKf = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/* ============================================================
 * MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER (works embedded inside Downloads wrapper or standalone)
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  /* Mesh background — só quando standalone (não embedded) */
  &:not([data-embedded])::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  .spin {
    animation: ${spinKf} 1s linear infinite;
  }

  /* ============================================================
   * HEADER (visível apenas em standalone)
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 14px;
    flex-shrink: 0;

    .header-icon { display: none; }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "DOWNLOADS · SPED";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};
          background: transparent !important;
          padding: 0 !important;
          border: none !important;
          opacity: 1 !important;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          background: transparent !important;
          padding: 0 !important;
          border: none !important;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * TABS HEADER (botão "Iniciar Download" inline em standalone)
   * ============================================================ */
  .tabs-header {
    display: flex;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${tokens.brand};
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      color: white;
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &.active {
        /* já é o estilo brand sólido */
      }
    }
  }

  /* ============================================================
   * KPI CARDS — padrão Certificados (vertical, thumb sutil)
   * ============================================================ */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .thumb {
      opacity: 0.35;
      font-size: 0.9em;
      color: ${({ theme }) => theme.text};
      display: inline-flex;
      align-items: center;
    }

    .text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .kpi-label {
      ${eyebrow};
      font-size: 0.55em;
      opacity: 0.5;
    }

    .kpi-value {
      ${mono};
      font-size: 1.4em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.02em;
      line-height: 1.1;
      margin-top: 4px;
    }

    /* Variantes: cor APENAS no thumb */
    &.primary .thumb  { color: ${({ theme }) => theme.text}; opacity: 0.4; }

    &.success .thumb  { color: ${tokens.brand}; opacity: 0.65; }
    &.rejected .thumb { color: ${tokens.danger}; opacity: 0.65; }
    &.warning .thumb  { color: ${tokens.warn}; opacity: 0.65; }
    &.info .thumb     { color: #3b82f6; opacity: 0.65; }

    /* Progress bar inferior */
    &.with-progress {
      padding-bottom: 0;
    }

    .kpi-progress {
      height: 3px;
      background: ${({ theme }) => surface(theme, 2)};
      border-radius: ${tokens.radiusPill};
      overflow: hidden;
      margin: 12px -18px -14px;

      .kpi-progress-fill {
        height: 100%;
        background: ${tokens.brand};
        border-radius: ${tokens.radiusPill};
        transition: width 0.5s ${tokens.easeOut};
      }
    }

    &.rejected .kpi-progress-fill {
      background: ${tokens.danger};
    }
  }

  /* ============================================================
   * TOOLBAR
   * ============================================================ */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 4px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
    }

    .toolbar-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: transparent;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.85;
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};
        opacity: 1;
      }
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 460px;
      min-width: 200px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      svg {
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        flex-shrink: 0;
      }

      input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }
    }
  }

  /* ============================================================
   * DOWNLOADS GRID
   * ============================================================ */
  .downloads-section {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    margin-top: 10px;
    padding: 4px 6px 24px 0;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 3px;
    }
  }

  .downloads-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;

    @media (max-width: 1400px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 720px)  { grid-template-columns: 1fr; }
  }

  /* ============================================================
   * DOWNLOAD CARD
   * ============================================================ */
  .download-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;
    min-height: 200px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 2px;
      background: ${tokens.brand};
      border-radius: 0 2px 2px 0;
      opacity: 0.55;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};
    }

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
      &::before { opacity: 1; }
      .delete-btn-corner { opacity: 1; }
    }

    .file-size-left {
      position: absolute;
      top: 12px;
      right: 14px;
      ${mono};
      font-size: 0.6em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .delete-btn-corner {
      position: absolute;
      top: 10px;
      right: 80px;
      width: 26px;
      height: 26px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      cursor: pointer;
      opacity: 0;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      svg { font-size: 0.85em; }

      &:hover {
        background: ${tokens.dangerSoft};
        border-color: rgba(239, 68, 68, 0.3);
        color: ${tokens.danger};
        opacity: 1;
      }
    }

    .download-header {
      display: grid;
      grid-template-columns: 32px 1fr;
      gap: 10px;
      align-items: flex-start;

      .download-icon {
        width: 32px;
        height: 32px;
        border-radius: ${tokens.radiusSm};
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        background: ${tokens.brandSoft};
        border: 1px solid ${tokens.brandRing};
        color: ${tokens.brand};
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg { font-size: 0.95em; }

        &.downloading {
          background: ${tokens.warnSoft};
          border-color: rgba(245, 158, 11, 0.3);
          color: ${tokens.warn};
        }
      }

      .download-info {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding-right: 80px;

        h5 {
          ${mono};
          margin: 0;
          font-size: 0.78em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.005em;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .periodo {
          ${mono};
          font-size: 0.62em;
          color: ${({ theme }) => theme.text};
          opacity: 0.55;
          letter-spacing: 0.01em;
        }

        .date {
          ${mono};
          font-size: 0.6em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          letter-spacing: 0.01em;
        }
      }
    }

    .download-progress-bar {
      position: relative;
      width: 100%;
      height: 4px;
      background: ${({ theme }) => surface(theme, 2)};
      border-radius: ${tokens.radiusPill};
      overflow: hidden;

      .download-progress-fill {
        height: 100%;
        background: ${tokens.brand};
        border-radius: ${tokens.radiusPill};
        transition: width 0.3s ${tokens.easeOut};
      }

      .download-progress-text {
        position: absolute;
        top: -16px;
        right: 0;
        ${mono};
        font-size: 0.6em;
        font-weight: 500;
        color: ${tokens.brand};
        letter-spacing: -0.01em;
      }
    }

    .download-meta {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;

      .meta-item {
        ${mono};
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 8px;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusPill};
        font-size: 0.62em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.85;
        letter-spacing: 0.02em;

        svg { font-size: 0.85em; opacity: 0.7; }

        &.success {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
          opacity: 1;

          svg { opacity: 0.95; }
        }

        &.error {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239, 68, 68, 0.22);
          opacity: 1;

          svg { opacity: 0.95; }
        }

        .meta-value {
          ${mono};
          font-size: 0.95em;
          letter-spacing: -0.005em;
        }
      }
    }

    .download-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex-wrap: wrap;
      margin-top: auto;
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};

      span:first-child {
        ${mono};
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 0.62em;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        letter-spacing: 0.01em;

        svg { font-size: 0.85em; opacity: 0.7; }
      }

      .percent-badge {
        ${mono};
        font-size: 0.6em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 2px 7px;
        border-radius: ${tokens.radiusPill};
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
      }
    }
  }

  /* ===== Card especial: Novo Download (CTA) ===== */
  .download-card.new-download {
    border: 1px dashed ${tokens.brandRing};
    background: transparent;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    text-align: center;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &::before { display: none; }

    &:hover {
      border-color: ${tokens.brand};
      background: ${tokens.brandSoft};
    }

    .new-download-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 16px;
    }

    .new-download-icon {
      width: 48px;
      height: 48px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};

      svg { font-size: 1.3em; }
    }

    .new-download-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;

      h5 {
        margin: 0;
        font-size: 0.92em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.01em;
      }

      p {
        margin: 0;
        ${mono};
        font-size: 0.66em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        letter-spacing: 0.01em;
        max-width: 220px;
        line-height: 1.5;
      }
    }
  }

  /* ============================================================
   * DELETE MODAL inline
   * ============================================================ */
  .delete-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.78);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 2em;
    animation: ${fadeInOnly} ${tokens.durBase} ${tokens.easeOut};
  }

  .delete-modal {
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusXl};
    width: 100%;
    max-width: 420px;
    padding: 28px 24px 22px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
    animation: ${fadeInOnly} ${tokens.durBase} ${tokens.easeOut};

    .delete-modal-icon {
      width: 56px;
      height: 56px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.dangerSoft};
      border: 1px solid rgba(239, 68, 68, 0.22);
      border-radius: 50%;
      color: ${tokens.danger};
      margin-bottom: 14px;

      svg { font-size: 1.4em; }
    }

    .delete-modal-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      margin-bottom: 16px;

      h4 {
        margin: 0 0 6px 0;
        font-size: 1.05em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.01em;
      }

      p {
        margin: 0;
        font-size: 0.82em;
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        line-height: 1.5;
      }

      .delete-modal-filename {
        ${mono};
        margin-top: 8px;
        padding: 6px 12px;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        font-size: 0.74em;
        color: ${({ theme }) => theme.text};
        opacity: 0.85;
        letter-spacing: 0.02em;
      }

      .delete-modal-warning {
        margin-top: 4px;
        font-size: 0.74em;
        color: ${tokens.danger};
        opacity: 0.75;
      }
    }

    .delete-modal-actions {
      display: flex;
      gap: 8px;
      width: 100%;

      button {
        flex: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 10px 16px;
        border-radius: ${tokens.radiusMd};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        font-weight: 500;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};
        letter-spacing: -0.005em;

        svg { font-size: 0.95em; }
      }

      .delete-modal-cancel {
        background: transparent;
        color: ${({ theme }) => theme.text};
        border: 1px solid ${({ theme }) => hairline(theme)};
        opacity: 0.85;

        &:hover {
          background: ${({ theme }) => surface(theme, 1)};
          opacity: 1;
        }
      }

      .delete-modal-confirm {
        background: ${tokens.danger};
        color: white;
        border: 1px solid ${tokens.danger};

        &:hover {
          filter: brightness(1.05);
          transform: translateY(-1px);
        }
      }
    }
  }
`;
